/**
 * OrderSourcePreview — the left "preview" pane of the desktop Design brief.
 *
 * Provenance-aware, but ONE consistent frame:
 *   - Orders with a scanned original (network PDFs/images) show that original
 *     via AuthenticatedDocumentFrame (auth'd blob → iframe).
 *   - Orders with no scan (manual, phone, walk-in, Stripe) show the app's
 *     generated order sheet (buildOrderSheetHtml) inline via <iframe srcDoc>.
 *
 * "Expand" pops the same document out full-screen. The inline pane is narrow,
 * so the browser's built-in PDF toolbar sheds its zoom controls; at full-screen
 * width those controls return, giving the designer real zoom (and clearer
 * access to page 2, where networks often put the product photo/recipe we can't
 * extract). We deliberately do NOT rebuild the viewer for inline zoom.
 */
import React, { useState } from 'react';
import { Maximize2, X } from 'lucide-react';
import { DocumentFrameView } from '@/components/AuthenticatedDocumentFrame';
import { useDocumentResource } from '@/hooks/useDocumentResource';
import { documentFailureMessage } from '@/utils/documentResource';
import { buildOrderSheetHtml } from '@/utils/printOrderSheet';

// Whether this order has a scanned original worth previewing. Stripe stores a
// text snapshot (not a scan), and manual/phone orders store nothing — both use
// the generated sheet instead.
function hasScannedOriginal(order) {
  const source = (order?.resolved_source || order?.network_source || '').toLowerCase();
  return !!order?.document_id && source !== 'stripe';
}

export default function OrderSourcePreview({ order, documentUrl, shop }) {
  const [expandedIdentity, setExpandedIdentity] = useState(null);
  const showOriginal = hasScannedOriginal(order);
  const sourceLabel = orderSourceLabel(order, order?.network_source || 'Manual');

  const resource = useDocumentResource({ url: showOriginal ? documentUrl : null,
    documentKey: JSON.stringify([order?.id, order?.document_id, sourceLabel, order?.desktop_sync?.origin]),
    orderNumber: order?.internal_order_number || order?.order_number });
  const expanded = expandedIdentity === resource.identity;
  const docFailed = resource.state !== 'loading' && resource.state !== 'available';

  // The order HAS a scanned original but it couldn't be fetched (offline, R2
  // down, missing/corrupt): fall back to the generated sheet, flagged so the
  // designer knows they're on the facsimile, not the source.
  const fallbackActive = showOriginal && docFailed;
  const showGeneratedSheet = !showOriginal || docFailed;
  const headerLabel = fallbackActive
    ? 'Generated sheet · original unavailable'
    : showOriginal
      ? `${sourceLabel} order`
      : 'Generated order sheet';
  const subLabel = showOriginal && !fallbackActive ? 'scrollable' : 'built from order data';

  // The document itself — rendered both inline and (when expanded) in the
  // full-screen overlay. `#view=FitH` fits the page to the frame width.
  const generatedSheetFrame = (
    <iframe
      srcDoc={buildOrderSheetHtml(order, shop)}
      className="w-full h-full border-0 bg-white"
      title="Generated order sheet"
      data-testid="preview-generated-sheet"
    />
  );
  const documentFrame = showGeneratedSheet ? generatedSheetFrame : (
    <DocumentFrameView
      resource={resource}
      hash="#view=FitH"
      className="w-full h-full border-0"
      title="Source document"
      orderNumber={order?.internal_order_number || order?.order_number}
      data-testid="preview-original-doc"
    />
  );

  // Amber "attention" treatment (matching the offline nav) only when a
  // should-exist original failed — not for orders that never had one.
  const headerClass = fallbackActive ? 'bg-amber-600 text-white' : 'bg-[#1f2733] text-stone-200';
  const subLabelClass = fallbackActive ? 'text-amber-100' : 'text-stone-400';
  const expandClass = fallbackActive ? 'text-white/90 hover:text-white' : 'text-stone-300 hover:text-white';

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="order-source-preview">
      <div className={`flex items-center justify-between gap-2 px-4 py-2 ${headerClass} flex-shrink-0`}>
        <span className="text-xs font-semibold tracking-wide truncate">{headerLabel}</span>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className={`text-[11px] ${subLabelClass} hidden lg:inline`}>
            {subLabel}
          </span>
          <button
            type="button"
            onClick={() => setExpandedIdentity(resource.identity)}
            className={`inline-flex items-center gap-1 text-[11px] font-semibold ${expandClass} transition-colors`}
            data-testid="preview-expand"
            title="Pop out full screen (with zoom)"
          >
            <Maximize2 className="h-3.5 w-3.5" /> Expand
          </button>
        </div>
      </div>
      {fallbackActive && (
        <div className="bg-amber-50 px-4 py-2 text-xs text-amber-900" role="status">
          {documentFailureMessage(resource)}{' '}
          <button type="button" onClick={resource.retry} className="font-semibold underline">Retry original document</button>
        </div>
      )}
      <div className="flex-1 min-h-0 bg-stone-100">
        {documentFrame}
      </div>

      {expanded && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col p-4" data-testid="preview-lightbox">
          <div className="flex items-center justify-between mb-2 flex-shrink-0">
            <span className={`text-sm font-semibold truncate ${fallbackActive ? 'text-amber-300' : 'text-white'}`}>{headerLabel}</span>
            <button
              type="button"
              onClick={() => setExpandedIdentity(null)}
              className="inline-flex items-center gap-1 text-sm font-semibold text-white/80 hover:text-white"
              data-testid="preview-lightbox-close"
              aria-label="Close"
            >
              <X className="h-5 w-5" /> Close
            </button>
          </div>
          <div className="flex-1 min-h-0 bg-white rounded-lg overflow-hidden">
            {documentFrame}
          </div>
        </div>
      )}
    </div>
  );
}
import { orderSourceLabel } from '@/utils/orderSource';
